import { createSelector } from 'reselect';
import first from 'lodash/first';
import {
    ROUTE_TYPES_VALUES,
    TABLE_SESSION_TYPE
} from 'src/modules/Restaurant';
import isUndefined from 'lodash/isUndefined';
import { ORDER_STATUS, BILL_TILE_TYPE, BILL_STATUS } from './constants';
import { selectFingerprint } from '../User';

export const selectOrders = (state) => state.order.orders;

export const selectBillSummary = (state) => state.order.sessionSummary;

export const selectSessionError = (state) => state.order.sessionError;

export const selectOrdersPrice = (state) => state.order.price;

export const selectOrdersPositionsNumber = (state) => state.order.positionsNumber;

export const selectAreOrdersFetched = (state) => state.order.areOrdersFetched;

export const selectIsOrderPositionAdded = (state) => state.order.isOrderPositionAdded;

export const selectOrderContext = (state) => state.order.context;

export const selectAddOrderPositionError = (state) => state.order.addOrderPositionError;

export const selectIsSubmitOrderPending = (state) => state.order.isSubmitOrderPending;

export const selectMenuPositionConfigurator = (state) => state.order.configurablePosition;

export const selectValidationErrors = (state) => state.order.validationErrors;

export const selectCanCreateOrders = (state) => state.order.canCreateOrders;

export const selectIsAskForBillPending = (state) => state.order.isAskForBillPending;

export const selectNotification = (state) => state.order.notification;

export const selectReinitialize = (state) => state.order.reinitialize;

export const selectIsAnyOrder = (state) => state.order.orders.length > 0;

export const selectIsPaymentAllow = (state) => state.order.canMakePayment;

export const selectExternalPayment = (state) => state.order.externalPayment;

export const selectSubmitOrderError = (state) => state.order.submitOrderError;

export const selectCancelOrderError = (state) => state.order.cancelOrderError;

export const selectRepaymentError = (state) => state.order.repaymentError;

export const selectAskForBillError = (state) => state.order.askForBillError;

export const selectIsReadOnlyMenu = createSelector(
    selectOrderContext,
    (context) => context?.routeType === ROUTE_TYPES_VALUES.MENU
                            || context?.routeType === ROUTE_TYPES_VALUES.AREA_NOT_ACTIVE
                            || context?.routeType === ROUTE_TYPES_VALUES.BILL_WAIT
                            || context?.routeType === ROUTE_TYPES_VALUES.RESTAURANT_CLOSED
);

export const selectOrderSummary = createSelector(
    selectOrdersPrice,
    selectOrdersPositionsNumber,
    (price, positions) => ({
        price,
        positions
    })
);

export const selectLastOrderStatus = createSelector(
    selectOrders,
    (orders) => {
        const order = first(orders);

        if (!order) {
            return undefined;
        }

        return order.status;
    }
);

export const selectLastBill = createSelector(
    selectOrders,
    (orders) => {
        const order = first(orders);

        if (!order) {
            return undefined;
        }

        return order;
    }
);

export const selectLastBillStatus = createSelector(
    selectLastBill,
    (order) => order?.billStatus || false
);

export const selectIsAddPositionDisabled = createSelector(
    selectOrderContext,
    selectLastBillStatus,
    (state) => state.restaurant.tableSessionType,
    (context, lastBillStatus, tableSessionType) => context?.routeType === ROUTE_TYPES_VALUES.BILL_WAIT
        || (tableSessionType === TABLE_SESSION_TYPE.PAYMENT_IN_ADVANCE
            && (lastBillStatus === BILL_STATUS.CLOSED || lastBillStatus === BILL_STATUS.PAYMENT_ERROR))
);

export const selectLastBillSummary = createSelector(
    selectLastBill,
    (order) => order?.summary
);

export const selectShouldCreateNewOrder = createSelector(
    selectLastOrderStatus,
    (status) => isUndefined(status) || status !== ORDER_STATUS.NEW
);

export const selectIsOrderContextDefined = createSelector(
    selectOrderContext,
    (context) => Boolean(context && context.sessionId && context.tableId)
);

export const selectIsAddingPositionAllowed = createSelector(
    selectIsOrderContextDefined,
    selectOrderContext,
    selectCanCreateOrders,
    (isContext, context, canCreateOrders) => isContext
        && ((context.routeType === ROUTE_TYPES_VALUES.RESTAURANT && canCreateOrders)
            || context.routeType === ROUTE_TYPES_VALUES.BILL_WAIT)
);

const selectIsOrderOpened = createSelector(
    selectLastOrderStatus,
    (status) => status === ORDER_STATUS.NEW
);

export const selectCanOrder = createSelector(
    selectOrders,
    selectIsOrderOpened,
    selectCanCreateOrders,
    (orders, isOrderOpened, canCreateOrders) => {
        const order = first(orders);

        if (!order) {
            return false;
        }

        return order.positions.length > 0
            && isOrderOpened
            && canCreateOrders;
    }
);

export const selectIsAnyPlacedOrder = createSelector(
    selectOrders,
    (orders) => {
        const firstOrder = first(orders);

        if (!firstOrder) {
            return false;
        }

        return orders.some((order) => order.status === ORDER_STATUS.PLACED || order.status === ORDER_STATUS.ACCEPTED);
    }
);

export const selectNewOrderPositionCount = createSelector(
    selectOrders,
    (orders) => {
        const order = first(orders);

        if (!order) {
            return 0;
        }

        if (order.status === ORDER_STATUS.NEW) {
            return order.positions.length;
        }

        return 0;
    }
);

export const selectPlacedAndAcceptedOrderPositionCount = createSelector(
    selectOrders,
    (state) => state.restaurant.tableSessionType,
    (orders, sessionType) => {
        if (!first(orders)) {
            return 0;
        }

        if (sessionType === TABLE_SESSION_TYPE.PAYMENT_IN_ADVANCE) {
            return orders
                .filter((order) => (order.status === ORDER_STATUS.ACCEPTED
                    || order.status === ORDER_STATUS.PLACED)
                    && order.billStatus === BILL_STATUS.PAID)
                .reduce((sumOfOrderPositions, order) => sumOfOrderPositions + order.positions.length, 0);
        }

        return orders
            .filter((order) => order.status === ORDER_STATUS.ACCEPTED || order.status === ORDER_STATUS.PLACED)
            .reduce((sumOfOrderPositions, order) => sumOfOrderPositions + order.positions.length, 0);
    }
);

export const selectAllOrdersClosed = createSelector(
    selectOrders,
    (state) => state.restaurant.tableSessionType,
    (orders, sessionType) => {
        if (sessionType === TABLE_SESSION_TYPE.PAYMENT_IN_ADVANCE) {
            return orders.every((order) => (order.billStatus === BILL_STATUS.PAID
                || order.billStatus === BILL_STATUS.ABORTED)
                && order.status === ORDER_STATUS.COMPLETED);
        }

        return orders.every((order) => order.billStatus === BILL_STATUS.PAID
            || order.billStatus === BILL_STATUS.ABORTED
            || order.billStatus === BILL_STATUS.CLOSED);
    }
);

export const selectClientOrders = createSelector(
    selectOrders,
    selectBillSummary,
    selectOrderSummary,
    selectFingerprint,
    (orders, bill, summary, fingerprint) => {
        const billSummary = bill
            ? [{
                type: BILL_TILE_TYPE.SUMMARY,
                clients: bill.clients.sort((a, b) => {
                    if ((a.fingerprint === fingerprint && b.fingerprint === fingerprint)
                        || (a.fingerprint !== fingerprint && b.fingerprint !== fingerprint)) {
                        return 0;
                    }

                    if (a.fingerprint === fingerprint && b.fingerprint !== fingerprint) {
                        return -1;
                    }

                    return 1;
                }),
                summary,
                id: 'summary'
            }]
            : [];

        return [
            ...billSummary,
            ...orders
        ];
    }
);

function getBillStatusAction({
    isPaymentAllow,
    canCreateOrders,
    canOrder,
    isAnyOrder,
    isAnyOrderPlaced,
    areAllOrdersClosed,
    tableSessionType
}) {
    if (isPaymentAllow && !canOrder) {
        return 'makePayment';
    }

    if (!canCreateOrders) {
        return 'bill';
    }

    if (isAnyOrder && !canOrder && !isAnyOrderPlaced) {
        return 'bill';
    }

    if (tableSessionType && areAllOrdersClosed) {
        return 'bill';
    }

    return null;
}

function getPreparationStatusAction({
    canOrder,
    isAnyOrderPlaced,
    areAllOrdersClosed,
    tableSessionType,
    lastBillStatus
}) {
    if (!canOrder
        && isAnyOrderPlaced
        && !areAllOrdersClosed
        && tableSessionType === TABLE_SESSION_TYPE.PAYMENT_IN_ADVANCE
        && lastBillStatus !== BILL_STATUS.PAID) {
        return 'payment';
    }

    if (!canOrder && isAnyOrderPlaced && !areAllOrdersClosed) {
        return 'inPreparation';
    }

    return null;
}

function getOrderStatusAction({ canOrder }) {
    if (canOrder) {
        return 'makeOrder';
    }

    return null;
}

export const selectOrderStatus = createSelector(
    selectCanCreateOrders,
    selectCanOrder,
    selectIsPaymentAllow,
    selectIsAnyPlacedOrder,
    selectIsAnyOrder,
    selectPlacedAndAcceptedOrderPositionCount,
    selectNewOrderPositionCount,
    selectAllOrdersClosed,
    selectLastBillStatus,
    (state) => state.restaurant.tableSessionType,
    (
        canCreateOrders,
        canOrder,
        isPaymentAllow,
        isAnyOrderPlaced,
        isAnyOrder,
        uncompletedOrderPositions,
        newOrderPositions,
        areAllOrdersClosed,
        lastBillStatus,
        tableSessionType
    ) => ({
        bill: {
            isActive: (!canOrder && (isPaymentAllow || !canCreateOrders))
                || (isAnyOrder && !canOrder && areAllOrdersClosed && tableSessionType === TABLE_SESSION_TYPE.PAYMENT_IN_ADVANCE),
            isDisabled: !isPaymentAllow && (
                (canCreateOrders && tableSessionType === TABLE_SESSION_TYPE.WAITER_PAYMENT)
                || ((canOrder || !areAllOrdersClosed) && tableSessionType === TABLE_SESSION_TYPE.PAYMENT_IN_ADVANCE)
            ),
            action: getBillStatusAction({
                isPaymentAllow,
                canCreateOrders,
                canOrder,
                isAnyOrder,
                isAnyOrderPlaced,
                areAllOrdersClosed,
                tableSessionType
            })
        },
        preparation: {
            isDisabled: !isAnyOrderPlaced || !canCreateOrders || areAllOrdersClosed,
            isActive: !canOrder && isAnyOrderPlaced && !areAllOrdersClosed,
            uncompletedOrderPositions,
            action: getPreparationStatusAction({
                canOrder,
                isAnyOrderPlaced,
                areAllOrdersClosed,
                tableSessionType,
                lastBillStatus
            })
        },
        order: {
            isActive: canOrder,
            isDisabled: !canOrder,
            action: getOrderStatusAction({ canOrder }),
            newOrderPositions
        }
    })
);

export const selectShouldShowSessionEndedMessage = createSelector(
    selectOrders,
    (state) => state.restaurant.tableSessionType,
    (orders, tableSessionType) => orders.length > 0
        && orders.every((order) => order.billStatus === BILL_STATUS.PAID)
        && tableSessionType === TABLE_SESSION_TYPE.WAITER_PAYMENT
);
